/**
 * Owner: Masa Bayu
 * File: qxcircle.service-social.ts
 * Date:Time: 2026-09-18 10:42
 * Description: QXCircle engine social ops — follow/comment/profile.
 */

import type { AccountBundle } from "../account/account-types";
import { qxCircleAuthorFromAccount } from "./qxcircle-account";
import { createQxCircleCommentWithNotification } from "./qxcircle-comment-action";
import { syncQxCirclePostCommentCount } from "./qxcircle-comment-write";
import {
  buildQxCircleCommentsResponse,
  deleteQxCircleCommentRecord,
  presentQxCircleComment,
} from "./qxcircle-comment-delete";
import { updateQxCircleCommentRecord } from "./qxcircle-comment-update";
import { buildQxCircleFollowList } from "./qxcircle-follow-graph";
import { toggleQxCircleFollowAuthor } from "./qxcircle-follow-action";
import {
  markAllQxCircleNotificationsRead,
  markQxCircleNotificationReadById,
} from "./qxcircle-notification-read";
import { buildQxCircleAuthorProfile } from "./qxcircle-profile-view";
import {
  buildQxCircleModerationQueue,
  queueQxCircleReport,
} from "./qxcircle-moderation-report";
import { buildQxCircleSearch } from "./qxcircle-search";
import type {
  CreateQxCircleCommentInput,
  QxCircleAuthorProfile,
  QxCircleComment,
  QxCircleCommentsResponse,
  QxCircleFollowListResponse,
  QxCircleFollowResult,
  QxCircleModerationResponse,
  QxCircleNotification,
  QxCircleNotificationsResponse,
  QxCircleReportResult,
  QxCircleSearchResponse,
} from "./qxcircle.types";
import { QxCircleServiceBase } from "./qxcircle.service";

export class QxCircleService extends QxCircleServiceBase {
  listFollowers(
    handleRaw: string,
    viewerHandle: string | null = null,
  ): QxCircleFollowListResponse {
    return buildQxCircleFollowList(
      handleRaw,
      'followers',
      this.posts,
      this.localFollows,
      this.localBlocks,
      viewerHandle,
    );
  }

  listFollowing(
    handleRaw: string,
    viewerHandle: string | null = null,
  ): QxCircleFollowListResponse {
    return buildQxCircleFollowList(
      handleRaw,
      'following',
      this.posts,
      this.localFollows,
      this.localBlocks,
      viewerHandle,
    );
  }

  followAuthor(
    handleRaw: string,
    viewerHandle: string | null = null,
  ): QxCircleFollowResult {
    const toggled = toggleQxCircleFollowAuthor(
      handleRaw,
      this.posts,
      this.localFollows,
      this.ntfSeq,
      viewerHandle,
    );
    this.ntfSeq = toggled.nextSeq;
    if (toggled.notification) {
      this.notifications.unshift(toggled.notification);
    }
    return toggled.result;
  }

  listComments(
    postId: string,
    accountId?: string | null,
  ): QxCircleCommentsResponse {
    this.requirePost(postId);
    return buildQxCircleCommentsResponse(postId, this.comments, accountId);
  }

  deleteComment(
    postId: string,
    commentId: string,
    accountId: string,
  ): { ok: true; id: string } {
    return deleteQxCircleCommentRecord(
      this.requirePost(postId),
      commentId,
      this.comments,
      accountId,
    );
  }

  updateComment(
    postId: string,
    commentId: string,
    body: string,
    accountId: string,
  ): QxCircleComment {
    this.requirePost(postId);
    return updateQxCircleCommentRecord(
      postId,
      commentId,
      body,
      this.comments,
      accountId,
    );
  }

  authorProfile(handleRaw: string): QxCircleAuthorProfile {
    return buildQxCircleAuthorProfile(
      handleRaw, this.posts, this.hiddenPosts, this.localBlocks,
      this.localFollows, this.localMutes, (p) => this.presentPost(p),
    );
  }

  listNotifications(): QxCircleNotificationsResponse {
    const items = [...this.notifications].sort(
      (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt),
    );
    return {
      items,
      unreadCount: items.filter((n) => !n.read).length,
      world: "qxcircle",
      slice: "profile",
    };
  }

  markNotificationRead(notificationId: string): QxCircleNotification {
    return markQxCircleNotificationReadById(this.notifications, notificationId);
  }

  markAllNotificationsRead(): QxCircleNotificationsResponse {
    markAllQxCircleNotificationsRead(this.notifications);
    return this.listNotifications();
  }

  search(queryRaw: string): QxCircleSearchResponse {
    return buildQxCircleSearch(
      queryRaw,
      this.posts,
      this.hiddenPosts,
      this.localBlocks,
      (p) => this.presentPost(p),
    );
  }

  reportPost(postId: string, reasonRaw: string): QxCircleReportResult {
    const queued = queueQxCircleReport(
      this.requirePost(postId),
      reasonRaw,
      this.reports,
      this.hiddenPosts,
      this.reportSeq,
    );
    this.reportSeq = queued.nextSeq;
    return queued.result;
  }

  moderationQueue(): QxCircleModerationResponse {
    return buildQxCircleModerationQueue(this.reports);
  }

  createComment(
    postId: string,
    input: CreateQxCircleCommentInput,
    account: AccountBundle,
  ): QxCircleComment {
    const post = this.requirePost(postId);
    const author = qxCircleAuthorFromAccount(account);
    const created = createQxCircleCommentWithNotification(
      post,
      {
        ...input,
        authorName: author.authorName,
        authorHandle: author.authorHandle,
        ownerAccountId: author.accountId,
      },
      this.comments,
      this.commentSeq,
      this.ntfSeq,
    );
    this.commentSeq = created.nextCommentSeq;
    this.ntfSeq = created.nextNotificationSeq;
    this.comments.push(created.comment);
    this.notifications.unshift(...created.notifications);
    syncQxCirclePostCommentCount(post, this.comments);
    return presentQxCircleComment(created.comment, author.accountId);
  }

}

export { QxCircleService as default };
